import { styled, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { SingleSelect } from '../../../components/SingleSelect/SingleSelect';
import { ModalComponent } from '../../../components/ModalComponent/ModalComponent';
import { Typography } from '../../../components/Typography/Typography';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { useReassignApprovalDelegates } from '../../../queries/hooks/useReassignApprovalDelegates';
import { useUsers } from '../../../queries/hooks/useUsers';
import { ApprovalDelegate } from '../../../queries/hooks/useApprovalDelegates';
import { SelectItem, UserRole } from '../../../types';
import { hasRole } from '../../../utils/roles';
import { extractErrorMessage } from '../../../utils/errorNotifier';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Delegations being reassigned. */
  targets: ApprovalDelegate[];
  onDone: () => void;
}

/** Change the delegate (toDealOwner) for one or more existing delegations. */
export const ReassignDelegatesModal = ({ isOpen, onClose, targets, onDone }: Props) => {
  const { colors } = useTheme();
  const { pushSuccessToast, pushErrorToast } = useToastMessage();
  const { data: users = [] } = useUsers('active');
  const reassign = useReassignApprovalDelegates();

  const [toOwnerId, setToOwnerId] = useState<number | null>(null);

  // Never offer a current source owner of the selected rows as the new delegate.
  const sourceOwnerIds = useMemo(
    () => new Set(targets.map((t) => t.fromDealOwner.id)),
    [targets]
  );

  const delegateOptions = useMemo<SelectItem[]>(
    () =>
      users
        .filter((u) => u.isActive && hasRole(u, UserRole.DEAL_OWNER) && !sourceOwnerIds.has(u.id))
        .map((u) => ({ id: u.id, value: u.name })),
    [users, sourceOwnerIds]
  );

  const handleClose = () => {
    setToOwnerId(null);
    onClose();
  };

  const handleSubmit = () => {
    if (toOwnerId == null || targets.length === 0) return;
    reassign.mutate(
      { ids: targets.map((t) => t.id), toDealOwnerId: toOwnerId },
      {
        onSuccess: () => {
          pushSuccessToast({
            message: `Redelegated ${targets.length} delegation${targets.length === 1 ? '' : 's'}.`,
          });
          setToOwnerId(null);
          onDone();
        },
        onError: (error) => pushErrorToast({ message: extractErrorMessage(error) }),
      }
    );
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={handleClose}
      title='Redelegate Approval'
      width='480px'
      actionButtonsProps={{
        onCancel: handleClose,
        onSubmit: handleSubmit,
        isSubmitDisabled: toOwnerId == null || reassign.isLoading,
        submitText: 'Redelegate',
        cancelText: 'Cancel',
      }}
    >
      <Content>
        <Typography variant='body' color={colors.text.secondary}>
          {`Choose a new delegate for ${targets.length} selected delegation${
            targets.length === 1 ? '' : 's'
          }.`}
        </Typography>
        <Field>
          <Typography variant='subtitle2' color={colors.text.main}>
            Delegate to
          </Typography>
          <SingleSelect
            options={delegateOptions}
            value={delegateOptions.find((o) => o.id === toOwnerId) ?? null}
            onChange={(_, value) => setToOwnerId(value ? Number(value.id) : null)}
            fieldPlaceholder='Select deal owner'
          />
        </Field>
      </Content>
    </ModalComponent>
  );
};

const Content = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 120px;
`;

const Field = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
